import {InfiniteData, UseInfiniteQueryOptions} from '@tanstack/react-query';
import {useCallback} from 'react';
import {InvenTypeVo} from '../../../../common/shared/api/gateway/enums/InvenTypeVo.enum';
import {PagewebError} from '../../../../errors/PagewebError.class';
import {
  useInfiniteInvenLikeListQuery,
  useInfiniteInvenPaidListQuery,
  useInfiniteInvenRecentListQuery,
  useInfiniteSearchInvenListQuery,
} from '../../../../wrapper/invenWrapper';
import {InvenListQueryProps, SearchInvenQueryProps} from '../type';
import {getInvenListQuery} from '../util/selector';

interface Props {
  invenType: InvenTypeVo;
  queryKey?: any;
  searchQueryKey?: any;
  isSearch?: boolean;
  sortType: string;
  categoryUid?: number;
  options?: Omit<
    UseInfiniteQueryOptions<
      InvenListQueryProps['data'],
      PagewebError,
      InfiniteData<InvenListQueryProps['data']>
    >,
    'queryKey' | 'initialPageParam' | 'getNextPageParam'
  >;
}

export function useInfiniteInvenListQuery({
  invenType,
  queryKey,
  searchQueryKey,
  isSearch = false,
  sortType,
  categoryUid,
  options,
}: Props) {
  const isRecent = invenType === InvenTypeVo.Recent && !isSearch;
  const isPaid = invenType === InvenTypeVo.Paid && !isSearch;
  const isLike = invenType === InvenTypeVo.Like && !isSearch;

  const getNextPageParam = useCallback(
    (
      data: InvenListQueryProps['data'],
      pages: InvenListQueryProps['data'][]
    ) => {
      const invenListQuery = getInvenListQuery({invenType, data});
      return invenListQuery?.isEnd
        ? undefined
        : {
            invenListInput: {
              page: pages.length,
              size: 25,
              sortType,
              categoryUid,
            },
          };
    },
    [invenType, sortType, categoryUid]
  );

  const getNextSearchPageParam = useCallback(
    (
      data: SearchInvenQueryProps['data'],
      pages: SearchInvenQueryProps['data'][]
    ) => {
      return data.searchInvenList?.isEnd
        ? undefined
        : {
            searchInvenListInput: {
              ...searchQueryKey.searchInvenListInput,
              page: pages.length,
            },
          };
    },
    [searchQueryKey]
  );

  const invenRecentQuery = useInfiniteInvenRecentListQuery(queryKey, {
    enabled: isRecent,
    initialPageParam: {
      invenListInput: {
        page: 0,
        size: 25,
        sortType,
        categoryUid,
      },
    },
    getNextPageParam,
    ...(options as object),
  });

  const invenPaidQuery = useInfiniteInvenPaidListQuery(queryKey, {
    enabled: isPaid,
    initialPageParam: {
      invenListInput: {
        page: 0,
        size: 25,
        sortType,
        categoryUid,
      },
    },
    getNextPageParam,
    ...(options as object),
  });
  const invenLikeQuery = useInfiniteInvenLikeListQuery(queryKey, {
    enabled: isLike,
    initialPageParam: {
      invenListInput: {
        page: 0,
        size: 25,
        sortType,
        categoryUid,
      },
    },
    getNextPageParam,
    ...(options as object),
  });
  const invenSearchQuery = useInfiniteSearchInvenListQuery(searchQueryKey, {
    enabled: isSearch,
    initialPageParam: {
      searchInvenListInput: {
        ...searchQueryKey?.searchInvenListInput,
        page: 0,
      },
    },
    getNextPageParam: getNextSearchPageParam,
    ...(options as object),
  });

  return isSearch
    ? invenSearchQuery
    : isRecent
      ? invenRecentQuery
      : isPaid
        ? invenPaidQuery
        : invenLikeQuery;
}
